import {
  Component,
  Element,
  Event,
  EventEmitter,
  Listen,
  Prop,
  State,
  Watch,
  h,
} from '@stencil/core';
import { BielService } from '../../services/biel-service';
import { AiIconType } from '../../utils/icon-types';
import { trapFocus } from '../../utils/focus-trap';

@Component({
  tag: 'biel-search',
  styleUrl: 'biel-search.css',
  shadow: true,
})
export class BielSearch {
  private debounce: number = null;
  private ignoreNextClick: boolean = false;

  // Define internal properties
  @Prop() escapeClose: boolean = true;
  @Prop() clickOutsideClose: boolean = true;
  @Prop() fetchData: boolean = true;
  @Prop({ mutable: true, reflect: true }) showSearchModal: boolean = false;

  // Define component properties
  @Prop() aiResultFragment: string = 'Ask AI to answer your question';
  @Prop() hideFilters: boolean = true;
  @Prop() hideAskAiButton: boolean = false;
  @Prop() hideNavigationButtons: boolean = false;
  @Prop() modalPosition: string = 'top-center';
  @Prop() project: string = '';
  @Prop() stacked: boolean = false;
  @Prop() aiIcon: AiIconType = 'biel';
  @Prop() hideAvatars: boolean = false;
  @Prop() cleanTitles: boolean = false;
  @Prop() hideDescriptions: boolean = false;

  // Define text properties
  @Prop() searchPlaceholder: string = 'Search or ask AI...';

  // Define biel-bot properties
  @Prop() bielId: string = `biel-search-${Math.random().toString(36).substring(2, 9)}`;
  @Prop() apiKey: string = '';
  @Prop() apiUrl: string = '';
  @Prop() disableInput: boolean = false;
  @Prop() email: string = '';
  @Prop() expandModal: boolean = false;
  @Prop() hideCloseButton: boolean = false;
  @Prop() hideExpandButton: boolean = false;
  @Prop() hideRefreshButton: boolean = false;
  @Prop({ mutable: true }) hideSettingsButton: boolean = false;
  @Prop({ mutable: true }) hideConnectButton: boolean = false;
  @Prop() hideTooltips: boolean = false;
  @Prop() hideFeedback: boolean = false;
  @Prop() hideSources: boolean = false;
  @Prop() showTermsModal: boolean = false;

  // Define biel-bot text properties
  @Prop() errorMessage403: string =
    'Oops! The request URL or API key is invalid or not authorized for this project.';
  @Prop() errorMessage404: string = 'Oops! We could not find the provided project ID.';
  @Prop() errorMessageDefault: string = 'Oops! Please try again later.';
  @Prop() footerText: string = 'AI answers may be inaccurate. Verify with official sources.';
  @Prop() headerTitle: string = 'Company Name AI';
  @Prop() inputPlaceholderText: string = 'Type your message';
  @Prop() sendButtonText: string = 'Send';
  @Prop() sourcesText: string = 'Sources';
  @Prop() assistantLabel: string = 'Assistant';
  @Prop() suggestedQuestions: string = '';
  @Prop() suggestedQuestionsTitle: string = 'Suggested questions';
  @Prop() termsCheckboxText: string = 'I have read and agree to the Terms & Conditions.';
  @Prop() termsDescription: string =
    "Please review our <a href='https://biel.ai/terms' target='_blank' rel='noopener'>Terms & Conditions</a> before proceeding.";
  @Prop() termsTitle: string = 'Chatbot Terms & Conditions';
  @Prop() welcomeMessage: string = '';
  @Prop() keepConversation: boolean = true;
  @Prop() refreshButtonText: string = 'Refresh';
  @Prop() expandButtonText: string = 'Expand';
  @Prop() collapseButtonText: string = 'Collapse';
  @Prop() closeButtonText: string = 'Close';
  @Prop() settingsButtonText: string = 'Settings';
  @Prop() connectButtonText: string = 'Connect';
  @Prop() thinkModeAutoText: string = 'Auto';
  @Prop() thinkModeFastText: string = 'Instant';
  @Prop() thinkModeThinkText: string = 'Think';
  @Prop() thinkModeAutoDescription: string = 'Balances speed and quality';
  @Prop() thinkModeFastDescription: string = 'Prioritizes speed';
  @Prop() thinkModeThinkDescription: string = 'Takes longer for better accuracy';
  @Prop() mcpUrlText: string = 'MCP URL';
  @Prop() mcpUrlDescription: string = 'Copy to clipboard';
  @Prop() mcpClaudeText: string = 'Claude Code';
  @Prop() mcpClaudeDescription: string = 'CLI command';
  @Prop() mcpCopilotText: string = 'Copilot (VS code)';
  @Prop() mcpCopilotDescription: string = 'Opens in a new tab';
  @Prop() mcpCursorText: string = 'Cursor';
  @Prop() mcpCursorDescription: string = 'Opens in a new tab';
  @Prop() mcpCopiedText: string = 'Copied!';
  @Prop() mcpUrlCopiedDescription: string = 'URL copied to clipboard';
  @Prop() mcpClaudeCopiedDescription: string = 'Command copied to clipboard';
  @Prop() thinkModeEnabled: boolean = false;
  @Prop() defaultThinkMode?: 'auto' | 'fast' | 'think';
  @Prop() metadata: string = '';
  @Prop() mcpServerUrl: string = '';

  // Define state properties
  @State() BielBotId: string = `biel-bot-${Math.random().toString(36).substring(2, 9)}`;
  @State() selectedFilterTitle: string = '';
  @State() filters: { title: string }[] = [];
  @State() lastSearchQuery: string = '';
  @Event() messageSent: EventEmitter<{ bielId: string; message: string }>;
  @State() searchId: string = '';
  @State() searchQuery: string = '';
  @State() searchResults: { title: string; fragment: string; url: string }[] = [];
  @State() showModal: boolean = false;
  @State() isSearchFullscreen: boolean = false;
  @State() showPoweredBy: boolean = true;

  @Element() el: HTMLElement;

  @Listen('closeSearchClicked', { target: 'body' })
  handleSearchClose(event: CustomEvent<{ bielId: string }> | string) {
    let id: string;
    if (typeof event === 'string') {
      id = event;
    } else if (event.detail.bielId) {
      id = event.detail.bielId;
    }

    if (this.bielId === id) {
      const modal = this.el.shadowRoot.querySelector('.biel-search-content') as HTMLElement;
      if (modal) {
        modal.style.animation = 'fadeOut 0.3s forwards';
        setTimeout(() => {
          this.showSearchModal = false;
          document.body.style.overflow = '';
        }, 299);
      }
    }
  }

  @Listen('bielSearchButtonClick', { target: 'body' })
  handleSearchOpen() {
    this.searchQuery = '';
    this.lastSearchQuery = '';
    this.searchResults = [];
    this.searchId = '';
    this.selectedFilterTitle = '';
  }

  @Listen('expandSearchClicked', { target: 'body' })
  handleSearchExpand(event: CustomEvent<{ bielId: string }>) {
    if (this.bielId === event.detail.bielId) {
      this.ignoreNextClick = true;
      this.isSearchFullscreen = !this.isSearchFullscreen;
      document.body.style.overflow = this.isSearchFullscreen ? 'hidden' : '';
    }
  }

  @Listen('bielBotButtonClick', { target: 'body' })
  handleSearchButtonClick() {
    if (!this.showModal) return;
    const modal = this.el.shadowRoot.querySelector('.biel-search-bot') as HTMLElement;
    if (modal) {
      modal.style.animation = 'fadeOut 0.3s forwards';
      setTimeout(() => {
        document.body.style.overflow = '';
        this.handleBotClose();
      }, 299);
    }
  }

  @Listen('inputChanged', { target: 'body' })
  handleInputChanged(event: CustomEvent<string> | string) {
    if (this.debounce !== null) {
      clearTimeout(this.debounce);
      this.debounce = null;
    }

    const delay = typeof event === 'string' ? 0 : 400; // 0.4s;

    this.debounce = window.setTimeout(() => {
      this.searchQuery = typeof event === 'string' ? event : event.detail;
      this.searchQuery !== '' ? this.sendQuery() : (this.searchResults = null);
      this.debounce = null;
    }, delay);
  }

  @Listen('askAiClicked', { target: 'body' })
  handleAskAiClick(event: CustomEvent<{ bielId: string }>) {
    const emittedBielId = event?.detail?.bielId;
    if (emittedBielId !== this.bielId) return;
    const wasFullscreen = this.isSearchFullscreen;
    const query = this.searchQuery;
    this.ignoreNextClick = true;
    this.showModal = false;
    // Close any standalone bots on the page (use dedicated event to avoid resetting search state)
    document.body.dispatchEvent(new CustomEvent('closeStandaloneBots', { bubbles: true }));
    this.handleSearchClose(this.bielId);
    setTimeout(() => {
      this.showModal = true;
      if (wasFullscreen) {
        const fullscreenEvent = new CustomEvent('fullscreenClicked', {
          bubbles: true,
          detail: { bielId: this.BielBotId },
        });
        document.body.dispatchEvent(fullscreenEvent);
      }
      if (query.trim()) {
        this.messageSent.emit({ bielId: this.BielBotId, message: query });
      }
    }, 350);
  }

  @Listen('closeBot', { target: 'body' })
  handleBotClose(event?: CustomEvent) {
    // When called directly (no event), always close
    if (!event) {
      this.showModal = false;
      return;
    }
    // When triggered by event, only handle closeBot from the inner biel-bot
    const path = event.composedPath();
    if (path.some((el: EventTarget) => el === this.el)) {
      this.showModal = false;
    }
  }

  @Listen('filterSelected', { target: 'body' })
  handleFilterSelected(event: CustomEvent<string>) {
    this.selectedFilterTitle = event.detail;
    this.handleInputChanged(this.searchQuery);
  }

  @Listen('resultClicked', { target: 'body' })
  handleResultClicked(event: CustomEvent<{ url: string; newTab: boolean }>) {
    this.clickResult(event.detail.url, event.detail.newTab);
  }

  modalContent: HTMLElement;
  bielService: BielService;

  // Element that had focus before the modal opened, so it can be restored on close
  private previouslyFocused: HTMLElement = null;

  @Watch('showSearchModal')
  handleShowSearchModalChange(isOpen: boolean, wasOpen: boolean) {
    if (isOpen === wasOpen) {
      return;
    }
    if (isOpen) {
      const active = document.activeElement as HTMLElement;
      this.previouslyFocused = active && active !== document.body ? active : null;
    } else {
      const target = this.previouslyFocused;
      this.previouslyFocused = null;
      if (target && typeof target.focus === 'function' && target.isConnected) {
        target.focus();
      }
    }
  }

  componentWillLoad() {
    if (this.fetchData) this.fetchProjectData();
  }

  // Bound here rather than in componentWillLoad so the listeners are
  // re-attached if the element is moved in the DOM and reconnected
  connectedCallback() {
    window.addEventListener('keydown', this.handleKeyPress);
    window.addEventListener('mousedown', this.handleMouseDown);
    window.addEventListener('click', this.handleMouseClick);
  }

  disconnectedCallback() {
    window.removeEventListener('keydown', this.handleKeyPress);
    window.removeEventListener('mousedown', this.handleMouseDown);
    window.removeEventListener('click', this.handleMouseClick);
  }

  async fetchProjectData() {
    this.bielService = new BielService(
      this.project,
      this.errorMessage404,
      this.errorMessage403,
      this.errorMessageDefault,
      this.apiKey,
      this.apiUrl
    );
    const result = await this.bielService.getProjectDetails();
    if (typeof result !== 'string') {
      this.showPoweredBy = !result.whitelabel;
      this.filters = result.filters || [];
      // Hide think mode button if project doesn't support it
      if (!result.think_mode) {
        this.hideSettingsButton = true;
      }
    }
  }

  async sendQuery() {
    // bielService is only created by fetchProjectData(), which componentWillLoad
    // skips when the consumer sets fetch-data="false". The inputChanged listener
    // is registered on body regardless, so a query can still reach this method
    // with no service to run it. Bail out instead of throwing.
    if (!this.bielService) return;
    this.lastSearchQuery = this.searchQuery;
    this.searchResults = await this.bielService.sendQuery(
      this.searchQuery,
      this.selectedFilterTitle
    );
  }

  async clickResult(url: string, newTab: boolean = false) {
    // searchClicked() is analytics only, so a missing service must never block
    // navigation to the result the user asked for.
    if (this.bielService) {
      await this.bielService.searchClicked();
    }
    if (!newTab) {
      this.handleSearchClose(this.bielId);
    }
    window.open(url, newTab ? '_blank' : '_self');
  }

  handleKeyPress = (event: KeyboardEvent) => {
    // Previously this fired on every Escape anywhere on the page, even with the
    // modal closed, so it swallowed Escape from the host site's own UI
    if (!this.showSearchModal) {
      return;
    }

    if (event.key === 'Escape' && this.escapeClose) {
      event.preventDefault();
      this.handleSearchClose(this.bielId);
      return;
    }

    if (event.key === 'Tab' && this.modalContent) {
      trapFocus(event, this.modalContent);
    }
  };

  // Whether the press that started this click began inside the widget.
  // Selecting text in a result and releasing past the edge must not close it.
  private pressStartedInside = false;

  handleMouseDown = (event: MouseEvent) => {
    this.pressStartedInside = event.composedPath().some((el: EventTarget) => el === this.el);
  };

  handleMouseClick = (event: MouseEvent) => {
    if (this.ignoreNextClick) {
      this.ignoreNextClick = false;
      return;
    }

    const startedInside = this.pressStartedInside;
    this.pressStartedInside = false;

    if (this.showSearchModal && this.clickOutsideClose && !startedInside) {
      const path = event.composedPath();
      const isInsideSearch = path.some((el: EventTarget) => el === this.el);
      if (!isInsideSearch) {
        this.handleSearchClose(this.bielId);
      }
    }
  };

  render() {
    return (
      <div class="biel-search-wrapper" part="wrapper">
        <biel-bot
          class="biel-search-bot"
          showModal={this.showModal}
          bielId={this.BielBotId}
          apiKey={this.apiKey}
          apiUrl={this.apiUrl}
          disableInput={this.disableInput}
          email={this.email}
          expandModal={this.expandModal}
          hideCloseButton={this.hideCloseButton}
          hideExpandButton={this.hideExpandButton}
          hideRefreshButton={this.hideRefreshButton}
          hideSettingsButton={this.hideSettingsButton}
          hideConnectButton={this.hideConnectButton}
          hideTooltips={this.hideTooltips}
          hideFeedback={this.hideFeedback}
          modalPosition={this.modalPosition}
          project={this.project}
          showTermsModal={this.showTermsModal}
          errorMessage404={this.errorMessage404}
          errorMessage403={this.errorMessage403}
          errorMessageDefault={this.errorMessageDefault}
          footerText={this.footerText}
          headerTitle={this.headerTitle}
          inputPlaceholderText={this.inputPlaceholderText}
          sendButtonText={this.sendButtonText}
          suggestedQuestionsTitle={this.suggestedQuestionsTitle}
          termsCheckboxText={this.termsCheckboxText}
          termsDescription={this.termsDescription}
          termsTitle={this.termsTitle}
          sourcesText={this.sourcesText}
          suggestedQuestions={this.suggestedQuestions}
          welcomeMessage={this.welcomeMessage}
          keepConversation={this.keepConversation}
          assistantLabel={this.assistantLabel}
          aiIcon={this.aiIcon}
          hideAvatars={this.hideAvatars}
          hideSources={this.hideSources}
          refreshButtonText={this.refreshButtonText}
          expandButtonText={this.expandButtonText}
          collapseButtonText={this.collapseButtonText}
          closeButtonText={this.closeButtonText}
          settingsButtonText={this.settingsButtonText}
          thinkModeEnabled={this.thinkModeEnabled}
          defaultThinkMode={this.defaultThinkMode}
          metadata={this.metadata}
          mcpServerUrl={this.mcpServerUrl}
          connectButtonText={this.connectButtonText}
          thinkModeAutoText={this.thinkModeAutoText}
          thinkModeFastText={this.thinkModeFastText}
          thinkModeThinkText={this.thinkModeThinkText}
          thinkModeAutoDescription={this.thinkModeAutoDescription}
          thinkModeFastDescription={this.thinkModeFastDescription}
          thinkModeThinkDescription={this.thinkModeThinkDescription}
          mcpUrlText={this.mcpUrlText}
          mcpUrlDescription={this.mcpUrlDescription}
          mcpClaudeText={this.mcpClaudeText}
          mcpClaudeDescription={this.mcpClaudeDescription}
          mcpCopilotText={this.mcpCopilotText}
          mcpCopilotDescription={this.mcpCopilotDescription}
          mcpCursorText={this.mcpCursorText}
          mcpCursorDescription={this.mcpCursorDescription}
          mcpCopiedText={this.mcpCopiedText}
          mcpUrlCopiedDescription={this.mcpUrlCopiedDescription}
          mcpClaudeCopiedDescription={this.mcpClaudeCopiedDescription}
          exportparts="wrapper: biel-bot-wrapper, overlay: biel-bot-overlay, modal: biel-bot-modal, header: biel-bot-header, body: biel-bot-body, footer: biel-bot-footer, biel-terms-modal-overlay, biel-terms-modal-modal, biel-terms-modal-close-button, biel-terms-modal-title, biel-terms-modal-description, biel-terms-modal-checkbox-container, biel-terms-modal-button-container, biel-terms-modal-decline-button, biel-terms-modal-accept-button, biel-header-container, biel-header-title-container, biel-header-title, biel-header-powered-by, biel-header-icons, biel-header-settings-button, biel-header-settings-dropdown, biel-header-refresh-button, biel-header-expand-button, biel-header-close-button, biel-chat-container, biel-chat-continue-container, biel-chat-continue-button, biel-chat-loading, biel-message-container, biel-message-label, biel-message-bubble, biel-message-content, biel-suggested-questions-container, biel-suggested-questions-title, biel-suggested-questions-list, biel-suggested-questions-question, biel-input-container, biel-input-textarea, biel-input-send-button"
        ></biel-bot>
        {this.showSearchModal && (
          <div
            class={`biel-search-content biel-search-content--${this.modalPosition} ${this.showSearchModal ? 'biel-search-content--open' : ''} ${this.isSearchFullscreen ? 'fullscreen' : ''}`}
            part="modal"
            role="dialog"
            aria-modal="true"
            aria-label={this.searchPlaceholder}
            ref={(el) => (this.modalContent = el)}
          >
            <div class="biel-search-header" part="header">
              <biel-search-header
                placeholderText={this.searchPlaceholder}
                hideFilters={this.hideFilters}
                filters={this.filters}
                bielId={this.bielId}
                searchQuery={this.searchQuery}
                hideExpandButton={this.hideExpandButton}
                isFullscreen={this.isSearchFullscreen}
                expandButtonText={this.expandButtonText}
                collapseButtonText={this.collapseButtonText}
                closeButtonText={this.closeButtonText}
                hideTooltips={this.hideTooltips}
                exportparts="container: biel-search-header-container, input-container: biel-search-header-input-container, expand-button: biel-search-header-expand-button, close-button: biel-search-header-close-button, filters-container: biel-search-header-filters-container, biel-search-input-container, biel-search-input-input, biel-search-filters-container, biel-search-filters-filter-button"
              ></biel-search-header>
            </div>
            <div class={`biel-search-body ${this.searchQuery !== '' ? 'results' : ''}`} part="body">
              <biel-search-body
                style={{ '--powered-by-extra': this.showPoweredBy ? '40px' : '0px' }}
                hideAskAiButton={this.hideAskAiButton}
                searchResults={this.searchResults}
                searchQuery={this.searchQuery}
                lastSearchQuery={this.lastSearchQuery}
                aiResultFragment={this.aiResultFragment}
                bielId={this.bielId}
                stacked={this.stacked}
                aiIcon={this.aiIcon}
                cleanTitles={this.cleanTitles}
                hideDescriptions={this.hideDescriptions}
                exportparts="container: biel-search-body-container, result-list: biel-search-body-result-list, loading: biel-search-body-loading, no-results: biel-search-body-no-results, biel-search-ask-ai-button-link, biel-search-ask-ai-button-container, biel-search-ask-ai-button-icon, biel-search-ask-ai-button-text-container, biel-search-ask-ai-button-title, biel-search-ask-ai-button-description, biel-search-result-link, biel-search-result-container, biel-search-result-title, biel-search-result-description"
              ></biel-search-body>
            </div>
            <div class="biel-search-footer" part="footer">
              {!this.hideNavigationButtons && (
                <div class="biel-search-footer__quick-icons-container" part="quick-icons">
                  <div class="biel-search-footer__quick-icon">
                    Navigate
                    <span class="biel-search-footer__quick-icon-command-key">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-arrow-up"
                      >
                        <path d="m5 12 7-7 7 7" />
                        <path d="M12 19V5" />
                      </svg>
                    </span>
                    <span class="biel-search-footer__quick-icon-command-key">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-arrow-down"
                      >
                        <path d="M12 5v14" />
                        <path d="m19 12-7 7-7-7" />
                      </svg>
                    </span>
                  </div>
                  <div class="biel-search-footer__quick-icon">
                    Select
                    <span class="biel-search-footer__quick-icon-command-key">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-corner-down-left"
                      >
                        <polyline points="9 10 4 15 9 20" />
                        <path d="M20 4v7a4 4 0 0 1-4 4H4" />
                      </svg>
                    </span>
                  </div>
                  <div class="biel-search-footer__quick-icon">
                    Close
                    <span class="biel-search-footer__quick-icon-command-key">Esc</span>
                  </div>
                </div>
              )}
              <div class="biel-search-footer__powered-by" part="powered-by">
                {this.showPoweredBy && (
                  <span>
                    Powered by{' '}
                    <a target="_blank" href="https://biel.ai">
                      biel.ai
                    </a>
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}
